import authInterceptor from "../AuthInterceptor";

// Several ranking pieces ask for the same read-only configuration in the very
// same render pass: the grid and the Rankings screen both read the grid
// preferences on mount, and Add Columns / Freeze Columns each pair the column
// config with those preferences. Sharing the promise while it is still in
// flight collapses those into a single request.
//
// This is de-duplication, not caching: the entry is dropped the moment the
// request settles, so a call made after one finishes always hits the network
// again and nothing is ever served stale.
const inFlightRequests = new Map();

const buildRequestKey = (name, config) => {
  try {
    return `${name}:${JSON.stringify(config?.params ?? {})}`;
  } catch {
    // A non-serialisable param object cannot be matched safely, so give it a
    // unique key and let the request through untouched.
    return `${name}:${Symbol("params").toString()}`;
  }
};

const dedupeRequest = (key, createRequest) => {
  const pending = inFlightRequests.get(key);
  if (pending) {
    return pending;
  }

  const request = createRequest().finally(() => {
    inFlightRequests.delete(key);
  });

  inFlightRequests.set(key, request);
  return request;
};

// After a write, any GET still in flight was started against the old server
// state. Dropping it from the map does not disturb the callers already awaiting
// it — it only stops the *next* caller from joining a now-outdated request.
const invalidateInFlight = (namePrefix) => {
  inFlightRequests.forEach((_value, key) => {
    if (key.startsWith(`${namePrefix}:`)) {
      inFlightRequests.delete(key);
    }
  });
};

export const RankingAPI = {
  // getRannkList: () =>
  //     authInterceptor.get(`ranking-api/rank-list-api/`)
  //         .then((response) => response.data)
  //         .catch((error) => error.response),
  getRannkList: (config = {}) =>
    authInterceptor
      .get(`ranking-api/rank-list-api/`, config)
      .then((response) => response.data)
      .catch((error) => error.response),
  resetRank: (config = {}) =>
    authInterceptor
      .get(`ranking-api/update-data-from-csv/`, config)
      .then((response) => response.data)
      .catch((error) => error.response),
  projectedCostSaving: (bodyData) =>
    authInterceptor
      .post(`ranking-api/projected-cost-saving/`, bodyData)
      .then((response) => response.data)
      .catch((error) => error.response),
  rankListFilter: (bodyData) =>
    authInterceptor
      .post(`ranking-api/rank-list-api/`, bodyData)
      .then((response) => response.data)
      .catch((error) => error.response),
  getStatusOptions: () =>
    dedupeRequest(buildRequestKey("getStatusOptions"), () =>
      authInterceptor
        .get(`ranking-api/statuses/`)
        .then((response) => response.data)
        .catch((error) => error.response),
    ),
  getFilterFieldOptions: (config = {}) =>
    dedupeRequest(buildRequestKey("getFilterFieldOptions", config), () =>
      authInterceptor
        .get(`ranking-api/rank-list-filter-options/`, config)
        .then((response) => response.data)
        .catch((error) => error.response),
    ),
  exportRankList: (config = {}) =>
    authInterceptor
      .get(`ranking-api/rank-list-api/`, { ...config, responseType: "blob" })
      .then((response) => response)
      .catch((error) => Promise.reject(error)),
  getColumnConfig: () =>
    dedupeRequest(buildRequestKey("getColumnConfig"), () =>
      authInterceptor
        .get(`ranking-api/column-config/`)
        .then((response) => response.data)
        .catch((error) => error.response),
    ),
  updateColumnConfig: (bodyData) => {
    invalidateInFlight("getColumnConfig");
    return authInterceptor
      .post(`ranking-api/column-config/`, bodyData)
      .then((response) => response.data)
      .catch((error) => error.response);
  },
  getRankGridPreferences: (config = {}) =>
    dedupeRequest(buildRequestKey("getRankGridPreferences", config), () =>
      authInterceptor
        .get(`ranking-api/rank-grid-preferences/`, config)
        .then((response) => response.data)
        .catch((error) => error.response),
    ),
  updateRankGridPreferences: (bodyData, config = {}) => {
    invalidateInFlight("getRankGridPreferences");
    return authInterceptor
      .put(`ranking-api/rank-grid-preferences/`, bodyData, config)
      .then((response) => response.data)
      .catch((error) => error.response);
  },
};
